import { useEffect, useMemo, useState } from "react";
import { Check, Copy, FolderOpen, Pencil, Plus, Search, Tag, Trash2, Upload, X } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

import { API } from "@/apiConfig";

const BASE_FAMILIES = ["Sophrologie", "Nouveau chemin, nouvel élan", "Zen", "Yoga", "Préparation mentale", "Tai-chi-chuan", "Hypnose", "Naturopathie", "Massage & relaxation", "Acupuncture & pratiques chinoises", "Diététique & nutrition"];
const UNCLASSIFIED = "Non classées";

const tagsOf = value => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (!value) return [];
  return String(value).split(/[,#]/).map(tag => tag.trim()).filter(Boolean);
};

const tagsFromInput = value => [...new Set(tagsOf(value).map(tag => tag.toLocaleLowerCase("fr")))];
const familyOf = item => item.family?.trim() || UNCLASSIFIED;

export default function GestionMedia() {
  const [media, setMedia] = useState([]);
  const [form, setForm] = useState({ title: "", alt: "", family: "", tags: "" });
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [query, setQuery] = useState("");
  const [familyFilter, setFamilyFilter] = useState("Toutes");
  const [viewMode, setViewMode] = useState("normal");

  const load = () => axios.get(`${API}/admin/media`)
    .then(response => setMedia(Array.isArray(response.data) ? response.data : []))
    .catch(() => toast.error("Impossible de charger la médiathèque"));

  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!editing) return undefined;
    const previousOverflow = document.body.style.overflow;
    const closeOnEscape = event => { if (event.key === "Escape") setEditing(null); };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [editing]);

  const familyOptions = useMemo(() => {
    const found = media.map(item => item.family?.trim()).filter(Boolean);
    return [...new Set([...BASE_FAMILIES, ...found])].sort((a, b) => {
      const aBase = BASE_FAMILIES.indexOf(a);
      const bBase = BASE_FAMILIES.indexOf(b);
      if (aBase !== -1 || bBase !== -1) return (aBase === -1 ? 99 : aBase) - (bBase === -1 ? 99 : bBase);
      return a.localeCompare(b, "fr");
    });
  }, [media]);

  const groups = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase("fr");
    const visible = media.filter(item => {
      const family = familyOf(item);
      if (familyFilter !== "Toutes" && family !== familyFilter) return false;
      if (!needle) return true;
      const haystack = [item.title, item.alt, family, ...tagsOf(item.tags)].filter(Boolean).join(" ").toLocaleLowerCase("fr");
      return haystack.includes(needle.replace(/^#/, ""));
    });

    const grouped = new Map();
    visible.forEach(item => {
      const family = familyOf(item);
      if (!grouped.has(family)) grouped.set(family, []);
      grouped.get(family).push(item);
    });
    if (!needle && familyFilter === "Toutes") {
      familyOptions.forEach(family => { if (!grouped.has(family)) grouped.set(family, []); });
    }

    const order = [...familyOptions, ...[...grouped.keys()].filter(name => !familyOptions.includes(name)).sort((a, b) => a.localeCompare(b, "fr"))];
    if (order.includes(UNCLASSIFIED)) {
      order.splice(order.indexOf(UNCLASSIFIED), 1);
      order.push(UNCLASSIFIED);
    }
    return order.filter(name => grouped.has(name)).map(name => ({ name, items: grouped.get(name) }));
  }, [familyFilter, familyOptions, media, query]);

  const handleFile = event => {
    const selected = event.target.files?.[0];
    setFile(selected || null);
    setPreview(selected ? URL.createObjectURL(selected) : "");
    if (selected && !form.title) {
      setForm(current => ({ ...current, title: selected.name.replace(/\.[^.]+$/, "").replace(/[-_]+/g, " ") }));
    }
  };

  const closeAdd = () => {
    setAdding(false);
    setFile(null);
    setPreview("");
    setForm({ title: "", alt: "", family: "", tags: "" });
  };

  const handleAdd = async event => {
    event.preventDefault();
    if (!file) { toast.error("Choisissez une image à téléverser"); return; }
    const body = new FormData();
    body.append("file", file);
    body.append("title", form.title || file.name.replace(/\.[^.]+$/, ""));
    body.append("alt", form.alt || form.title || file.name.replace(/\.[^.]+$/, ""));
    body.append("family", form.family.trim());
    body.append("tags", JSON.stringify(tagsFromInput(form.tags)));
    await axios.post(`${API}/admin/media/upload`, body);
    toast.success("Image ajoutée à la médiathèque");
    closeAdd();
    load();
  };

  const startEditing = item => setEditing({
    id: item.id,
    url: item.url,
    title: item.title || "",
    alt: item.alt || "",
    family: item.family || "",
    tags: tagsOf(item.tags).join(", "),
    width: item.width,
    height: item.height,
    source: item.source,
  });

  const saveEditing = async event => {
    event.preventDefault();
    await axios.put(`${API}/admin/media/${editing.id}`, {
      title: editing.title.trim(),
      alt: editing.alt.trim(),
      family: editing.family.trim(),
      tags: tagsFromInput(editing.tags),
    });
    toast.success("Classement de l’image mis à jour");
    setEditing(null);
    load();
  };

  const handleDelete = async id => {
    if (!window.confirm("Supprimer cette image de la médiathèque ?")) return;
    await axios.delete(`${API}/admin/media/${id}`);
    toast.success("Image supprimée");
    load();
  };

  const copyUrl = url => {
    navigator.clipboard.writeText(url);
    toast.success("URL copiée");
  };

  const metadataFields = (value, setValue) => (
    <>
      <div>
        <label className="mb-1.5 block font-manrope text-xs text-charcoal-muted">Titre *</label>
        <input value={value.title} onChange={event => setValue({ ...value, title: event.target.value })} placeholder="Nom de l’image" className="w-full rounded-xl border border-sage/20 px-4 py-2.5 font-manrope text-sm outline-none focus:border-sage" />
      </div>
      <div>
        <label className="mb-1.5 block font-manrope text-xs text-charcoal-muted">Texte alternatif</label>
        <input value={value.alt} onChange={event => setValue({ ...value, alt: event.target.value })} placeholder="Décrivez ce que montre l’image" className="w-full rounded-xl border border-sage/20 px-4 py-2.5 font-manrope text-sm outline-none focus:border-sage" />
      </div>
      <div>
        <label className="mb-1.5 block font-manrope text-xs text-charcoal-muted">Famille / type</label>
        <input list="media-family-options" value={value.family} onChange={event => setValue({ ...value, family: event.target.value })} placeholder="Sophrologie, Zen, Yoga…" className="w-full rounded-xl border border-sage/20 px-4 py-2.5 font-manrope text-sm outline-none focus:border-sage" />
      </div>
      <div>
        <label className="mb-1.5 block font-manrope text-xs text-charcoal-muted">Tags</label>
        <input value={value.tags} onChange={event => setValue({ ...value, tags: event.target.value })} placeholder="#homme, #salutation, #soleil" className="w-full rounded-xl border border-sage/20 px-4 py-2.5 font-manrope text-sm outline-none focus:border-sage" />
      </div>
    </>
  );

  return (
    <div className="space-y-6">
      <datalist id="media-family-options">
        {familyOptions.map(family => <option key={family} value={family} />)}
      </datalist>

      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="mb-1 font-manrope text-[11px] font-semibold uppercase tracking-[.22em] text-sage">Contenus</p>
          <h1 className="font-playfair text-3xl text-charcoal">Médiathèque</h1>
          <p className="mt-1 font-manrope text-sm text-charcoal-muted">{media.length} ressource{media.length > 1 ? "s" : ""}, organisées par univers</p>
        </div>
        <button onClick={() => { setAdding(true); setEditing(null); }} data-testid="add-media-btn" className="btn-primary flex items-center justify-center gap-2"><Plus size={16} /> Ajouter une image</button>
      </div>

      <div className="grid gap-3 rounded-2xl border border-sage/10 bg-white p-3 shadow-sm md:grid-cols-[220px_auto_minmax(0,1fr)]">
        <div className="flex min-h-10 items-center rounded-xl border border-sage/15 bg-bone/30 p-1" role="group" aria-label="Mode d’affichage de la médiathèque">
          <button type="button" aria-pressed={viewMode === "normal"} onClick={() => setViewMode("normal")} className={`flex-1 rounded-lg px-3 py-1.5 font-manrope text-xs font-semibold transition-colors ${viewMode === "normal" ? "bg-sage text-white shadow-sm" : "text-charcoal-muted hover:text-charcoal"}`}>Normal</button>
          <button type="button" aria-pressed={viewMode === "compact"} onClick={() => setViewMode("compact")} className={`flex-1 rounded-lg px-3 py-1.5 font-manrope text-xs font-semibold transition-colors ${viewMode === "compact" ? "bg-sage text-white shadow-sm" : "text-charcoal-muted hover:text-charcoal"}`}>Compact</button>
        </div>
        <label className="flex items-center gap-2 rounded-xl border border-sage/15 px-3 text-charcoal-muted">
          <FolderOpen size={16} />
          <select value={familyFilter} onChange={event => setFamilyFilter(event.target.value)} className="w-full bg-transparent py-2.5 font-manrope text-sm text-charcoal outline-none">
            <option>Toutes</option>
            {familyOptions.map(family => <option key={family}>{family}</option>)}
            {media.some(item => !item.family?.trim()) && <option>{UNCLASSIFIED}</option>}
          </select>
        </label>
        <label className="flex items-center gap-2 rounded-xl bg-bone/60 px-3 text-charcoal-muted transition-colors focus-within:bg-bone">
          <Search size={16} />
          <input value={query} onChange={event => setQuery(event.target.value)} placeholder="Rechercher un titre, une famille ou #soleil" className="w-full bg-transparent py-2.5 font-manrope text-sm text-charcoal outline-none" />
        </label>
      </div>

      {adding && (
        <form onSubmit={handleAdd} className="rounded-2xl border border-sage/15 bg-white p-5 shadow-sm">
          <div className="mb-4 flex items-center justify-between">
            <div><p className="font-manrope font-medium text-charcoal">Ajouter une ressource</p><p className="font-manrope text-xs text-charcoal-muted">Classez-la maintenant pour la retrouver rapidement.</p></div>
            <button type="button" onClick={closeAdd} className="p-1.5 text-charcoal-muted hover:text-charcoal"><X size={18} /></button>
          </div>
          <div className="grid gap-5 lg:grid-cols-[220px_minmax(0,1fr)]">
            <div>
              <label className="flex h-48 cursor-pointer flex-col items-center justify-center gap-2 overflow-hidden rounded-xl border border-dashed border-sage/30 bg-bone/40 px-4 text-center font-manrope text-sm text-charcoal-muted transition-all hover:border-sage hover:bg-sage/5">
                {preview ? <img src={preview} alt="Aperçu" className="h-full w-full object-contain p-2" /> : <><Upload size={20} className="text-sage" /><span>Choisir une image</span><span className="text-[11px]">PNG, JPG, WebP</span></>}
                <input type="file" accept="image/*" onChange={handleFile} className="hidden" />
              </label>
              {file && <p className="mt-2 truncate font-manrope text-xs text-charcoal-muted">{file.name}</p>}
            </div>
            <div className="grid content-start gap-4 md:grid-cols-2">
              {metadataFields(form, setForm)}
              <div className="flex gap-3 md:col-span-2">
                <button type="submit" data-testid="media-save-btn" className="btn-primary flex items-center gap-2"><Plus size={15} /> Téléverser</button>
                <button type="button" onClick={closeAdd} className="btn-secondary">Annuler</button>
              </div>
            </div>
          </div>
        </form>
      )}

      {groups.length === 0 ? (
        <div className="rounded-2xl border border-sage/10 bg-white py-16 text-center font-manrope text-charcoal-muted">Aucune image ne correspond à cette recherche.</div>
      ) : groups.map(group => (
        <section key={group.name} className="space-y-3">
          <div className="flex items-center gap-4">
            <div className="flex shrink-0 items-center gap-2">
              <FolderOpen size={16} className="text-sage" />
              <h2 className="font-manrope text-sm font-semibold uppercase tracking-[.16em] text-charcoal">{group.name}</h2>
              <span className="rounded-full bg-sage/10 px-2 py-0.5 font-manrope text-[11px] font-medium text-sage">{group.items.length}</span>
            </div>
            <div className="h-px flex-1 bg-sage/20" />
          </div>
          {group.items.length === 0 ? (
            <div className="rounded-xl border border-dashed border-sage/15 px-4 py-6 text-center font-manrope text-xs text-charcoal-muted">Aucune image dans cette famille</div>
          ) : (
            <div className={viewMode === "compact" ? "grid grid-cols-[repeat(auto-fill,136px)] gap-1.5" : "grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-4"}>
              {group.items.map((item, index) => (
                <article key={item.id || index} data-testid={`media-item-${index}`} className={`group overflow-hidden border bg-white transition-all ${viewMode === "compact" ? "rounded-xl" : "rounded-2xl"} ${editing?.id===item.id?"border-sage ring-2 ring-sage/20":"border-sage/10"}`}>
                  <div className={`relative bg-bone/70 ${viewMode === "compact" ? "aspect-square" : "aspect-[400/243] p-2"}`}>
                    <img src={item.url} alt={item.alt || item.title} className={`h-full w-full ${viewMode === "compact" ? "object-cover" : "rounded-lg object-contain"}`} onError={event => { event.currentTarget.style.opacity = "0"; }} />
                    <div className="absolute inset-0 flex items-center justify-center gap-2 bg-black/0 opacity-0 transition-all group-hover:bg-black/35 group-hover:opacity-100 group-focus-within:bg-black/35 group-focus-within:opacity-100">
                      <button type="button" title="Copier l’URL" onClick={() => copyUrl(item.url)} className="rounded-lg bg-white p-2 text-charcoal shadow hover:text-sage"><Copy size={14} /></button>
                      <button type="button" title="Modifier le classement" onClick={() => startEditing(item)} className="rounded-lg bg-white p-2 text-charcoal shadow hover:text-sage"><Pencil size={14} /></button>
                      <button type="button" title="Supprimer" onClick={() => handleDelete(item.id)} className="ml-4 rounded-lg bg-white p-2 text-charcoal shadow hover:text-red-500"><Trash2 size={14} /></button>
                    </div>
                  </div>
                  <div className={viewMode === "compact" ? "p-2" : "space-y-2 p-3"}>
                    <div>
                      <p className="truncate font-manrope text-xs font-medium text-charcoal" title={item.title}>{item.title}</p>
                      {viewMode !== "compact" && item.alt && <p className="mt-0.5 truncate font-manrope text-[11px] text-charcoal-muted">{item.alt}</p>}
                    </div>
                    {viewMode !== "compact" && tagsOf(item.tags).length > 0 && (
                      <div className="flex flex-wrap items-center gap-1">
                        <Tag size={11} className="mr-0.5 text-sage" />
                        {tagsOf(item.tags).slice(0, 4).map(tag => <span key={tag} className="rounded-full bg-bone px-1.5 py-0.5 font-manrope text-[10px] text-charcoal-muted">#{tag}</span>)}
                        {tagsOf(item.tags).length > 4 && <span className="font-manrope text-[10px] text-charcoal-muted">+{tagsOf(item.tags).length - 4}</span>}
                      </div>
                    )}
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      ))}

      {editing && (
        <div className="fixed inset-0 z-[80] flex items-end justify-center lg:items-center lg:p-6" role="dialog" aria-modal="true" aria-labelledby="media-editor-title">
          <button type="button" onClick={() => setEditing(null)} aria-label="Fermer l’éditeur de l’image" className="absolute inset-0 bg-charcoal/55 backdrop-blur-[2px]" />
          <form onSubmit={saveEditing} className="relative z-10 flex max-h-[100dvh] w-full flex-col overflow-hidden bg-white shadow-2xl lg:max-h-[calc(100vh-3rem)] lg:max-w-5xl lg:rounded-3xl">
            <div className="flex items-start justify-between gap-4 border-b border-sage/10 px-5 py-4 lg:px-6">
              <div><p className="font-manrope text-[11px] font-semibold uppercase tracking-[.2em] text-sage">Médiathèque</p><h2 id="media-editor-title" className="font-playfair text-2xl text-charcoal">Modifier l’image</h2><p className="font-manrope text-xs text-charcoal-muted">L’image et ses attributs restent visibles ensemble.</p></div>
              <button type="button" onClick={() => setEditing(null)} aria-label="Fermer" className="rounded-full border border-sage/15 p-2 text-charcoal-muted transition-colors hover:bg-bone hover:text-charcoal"><X size={19} /></button>
            </div>
            <div className="grid min-h-0 flex-1 overflow-y-auto lg:grid-cols-[minmax(0,1.15fr)_minmax(360px,.85fr)] lg:overflow-hidden">
              <figure className="flex min-h-64 items-center justify-center bg-charcoal p-4 lg:min-h-[480px] lg:p-8">
                <div className="w-full">
                  <img src={editing.url} alt={editing.alt || editing.title} className="mx-auto max-h-[52vh] w-full rounded-xl object-contain shadow-lg lg:max-h-[62vh]" />
                  <figcaption className="mt-3 flex flex-wrap justify-center gap-x-3 gap-y-1 font-manrope text-[11px] text-white/65"><span>{editing.title || "Image sans titre"}</span>{editing.width&&editing.height&&<span>{editing.width} × {editing.height}px</span>}{editing.source&&<span>Source : {editing.source}</span>}</figcaption>
                </div>
              </figure>
              <div className="flex flex-col lg:min-h-0">
                <div className="grid flex-1 content-start gap-4 overflow-y-auto p-5 lg:p-6">{metadataFields(editing, setEditing)}<div className="rounded-xl bg-bone/70 p-3 font-manrope text-xs text-charcoal-muted"><p className="font-semibold text-charcoal">Conseil de classement</p><p className="mt-1">La famille crée la section visible dans la médiathèque. Les tags facilitent ensuite les recherches transversales.</p></div></div>
                <div className="flex shrink-0 gap-3 border-t border-sage/10 bg-white p-4 lg:px-6">
                  <button type="submit" className="btn-primary flex items-center gap-2"><Check size={15} /> Enregistrer</button>
                  <button type="button" onClick={() => setEditing(null)} className="btn-secondary">Annuler</button>
                </div>
              </div>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
